import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import {
  matchFieldsValidator,
  PASSWORD_POLICY_MAX_LENGTH,
  PASSWORD_POLICY_MIN_LENGTH,
  passwordPolicyValidator,
} from '../../validators';
import {
  FormLineComponent,
  FormLineWidthVariant,
} from '../form-line/form-line.component';

export type NewPasswordFormGroup = FormGroup<{
  password: FormControl<string>;
  passwordConfirmation: FormControl<string>;
}>;

@Component({
  selector: 'app-new-password-fields',
  imports: [
    ReactiveFormsModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputGroupComponent,
    NzInputDirective,
    NzIconModule,
    FormLineComponent,
  ],
  templateUrl: './new-password-fields.component.html',
  styleUrl: './new-password-fields.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewPasswordFieldsComponent {
  readonly formGroup = input.required<NewPasswordFormGroup>();
  readonly widthVariant = input<FormLineWidthVariant>('half');
  readonly disallowedTerms = input<readonly string[]>([]);

  protected readonly passwordVisible = signal(false);
  protected readonly confirmationVisible = signal(false);
  protected readonly minLength = PASSWORD_POLICY_MIN_LENGTH;
  protected readonly maxLength = PASSWORD_POLICY_MAX_LENGTH;

  constructor() {
    effect(() => {
      const group = this.formGroup();
      const disallowedTerms = this.disallowedTerms();
      const { password, passwordConfirmation } = group.controls;

      password.setValidators([
        Validators.required,
        Validators.minLength(PASSWORD_POLICY_MIN_LENGTH),
        Validators.maxLength(PASSWORD_POLICY_MAX_LENGTH),
        passwordPolicyValidator({ disallowedTerms }),
      ]);
      passwordConfirmation.setValidators(Validators.required);
      password.updateValueAndValidity({ emitEvent: false });
      passwordConfirmation.updateValueAndValidity({
        emitEvent: false,
      });

      group.setValidators([
        matchFieldsValidator({
          source: 'password',
          target: 'passwordConfirmation',
          errorKey: 'passwordMismatch',
        }),
      ]);
      group.updateValueAndValidity({ emitEvent: false });
    });
  }

  protected handleTogglePassword(): void {
    this.passwordVisible.update((visible) => !visible);
  }

  protected handleToggleConfirmation(): void {
    this.confirmationVisible.update((visible) => !visible);
  }
}
